import { useEffect, useState } from 'react';
import * as turf from '@turf/turf';
import * as projection from '@turf/projection';
import type { Feature, LineString, GeoJsonProperties } from 'geojson';
import type { AirportType } from '../utils/types';

type UseRouteLineProps = {
  originAirport: AirportType | Record<string, never>;
  destinationAirport: AirportType | Record<string, never>;
};

const useRouteLine = ({ originAirport, destinationAirport }: UseRouteLineProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [curved, setCurved] = useState<Feature<LineString, GeoJsonProperties>>([] as any);
  useEffect(() => {
    setCurved([] as unknown as Feature<LineString, GeoJsonProperties>);
    if (originAirport.airport_name && destinationAirport.airport_name) {
      const start = projection.toWgs84([
        parseFloat(originAirport.longitude),
        parseFloat(originAirport.latitude),
      ]);
      const end = projection.toWgs84([
        parseFloat(destinationAirport.longitude),
        parseFloat(destinationAirport.latitude),
      ]);
      const distance = turf.distance(start, end);
      const midpoint = turf.midpoint(start, end);
      const bearing = turf.bearing(start, end) - 90;
      const destination = turf.destination(midpoint, distance, bearing);

      const curvedLine = turf.lineArc(
        destination,
        turf.distance(destination, start),
        turf.bearing(destination, end),
        turf.bearing(destination, start),
        { steps: 128 },
      );
      const mercatorCurvedLine = projection.toMercator(curvedLine);

      setCurved(mercatorCurvedLine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originAirport, destinationAirport]);

  return curved;
};

export default useRouteLine;
